/**
 * `getTenantInfo()` — the swap-seam for the active tenant's brand + features.
 *
 * Wraps the shared `apiRequest<T>()` so it routes through MSW today (mock-first,
 * before E25-1 #175 lands) and to the real `/api/tenant-info` later with NO
 * caller changes. On any failure (no backend / 404 before E25-1) the caller
 * (`BrandProvider`) falls back to `MAC_DEFAULT` so the app is never blank — the
 * same graceful-degradation contract as `getMe()` in `client.ts`.
 */
import { apiRequest } from "./client";
import type { TenantInfo } from "./tenantInfo.types";

/**
 * Built-in MAC tenant manifest. The colours are EXACTLY today's `index.css`
 * `:root` accent family (`#007AFF` / `#0066D6`), so running as MAC reproduces the
 * current dashboard byte-for-byte even when the endpoint is unreachable. This is
 * also the MSW fixture's source of truth for the `mac` tenant.
 */
export const MAC_DEFAULT: TenantInfo = {
  brand: {
    slug: "mac",
    name: "Command Center",
    shortName: "MAC",
    logoUrl: null,
    colors: {
      accent: "#007AFF",
      accentHover: "#0066D6",
      accentText: "#0066D6",
      accentSubtle: "#EBF5FF",
      sidebar: "#0F1729",
    },
  },
  // MAC has every surface enabled — gating only ever HIDES links for tenants
  // that lack a feature; MAC's nav is unchanged.
  features: [
    "members",
    "renewals",
    "billing",
    "finance",
    "events",
    "store",
    "orgs",
    "scorecard",
    "feedback",
  ],
  // The default identity is an admin with full capabilities — this is the
  // first-paint / loading state (a real user's role+capabilities replace it once
  // the authenticated `/api/tenant-info` fetch resolves). "*" = every module.
  role: "admin",
  capabilities: ["*"],
};

/**
 * The minimal NEUTRAL shell used when `/api/tenant-info` fails (E32-3). It grants
 * NO features and NO capabilities (so no module leaks) and carries a generic,
 * client-facing name that never exposes the internal product name. BrandProvider
 * renders this + a recoverable retry instead of silently falling back to
 * all-features-on.
 */
export const NEUTRAL_BRAND = {
  slug: "",
  name: "Command Center",
  shortName: "",
  logoUrl: null,
  colors: { accent: "#007AFF" },
} as const;

/** Fetch the active tenant's brand + feature manifest. */
export function getTenantInfo(): Promise<TenantInfo> {
  return apiRequest<TenantInfo>("/tenant-info");
}
